import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PortalService, PortalPage } from '../my-portals/portal.service';
import { MyPortalComponent } from '../../../partials/my-portal/my-portal.component';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { ToastService } from '../../../services/notification/toast.service';

type StatusFilter = 'all' | 'active' | 'inactive';

@Component({
    selector: 'app-portal-list',
    templateUrl: './portal-list.component.html',
    styleUrls: ['./portal-list.component.scss'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, MyPortalComponent]
})
export class PortalListComponent implements OnInit {
    @Input() partnerId: number | null = null;

    page: PortalPage | null = null;
    pageIndex = 0;
    pageSize = 5;
    readonly pageSizeOptions = [5, 10, 20];
    statusFilter: StatusFilter = 'all';
    searchControl = new FormControl('', { nonNullable: true });
    listLoading = false;
    hasPortals = false;

    private firstLoad = true;
    private destroyRef = inject(DestroyRef);

    constructor(private portalService: PortalService,
                private spinner: SpinnerService,
                private toast: ToastService,
                private router: Router) {
    }

    ngOnInit(): void {
        this.portalService.hasPortals
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(has => this.hasPortals = has);

        this.searchControl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.pageIndex = 0;
                this.loadPage();
            });

        this.loadPage();
    }

    get showToolbar(): boolean {
        return this.hasPortals;
    }

    get showEmptyState(): boolean {
        return this.partnerId === null || (!this.hasPortals && !this.listLoading);
    }

    get pageNumbers(): number[] {
        const total = this.page?.pages ?? 0;
        // Ventana de máximo 5 páginas centrada en la actual
        const start = Math.max(0, Math.min(this.pageIndex - 2, total - 5));
        const end = Math.min(total, start + 5);
        const numbers: number[] = [];
        for (let i = start; i < end; i++) {
            numbers.push(i);
        }
        return numbers;
    }

    clearSearch(): void {
        this.searchControl.setValue('');
    }

    setStatusFilter(filter: StatusFilter): void {
        if (this.statusFilter === filter) {
            return;
        }
        this.statusFilter = filter;
        this.pageIndex = 0;
        this.loadPage();
    }

    setPageSize(size: number): void {
        this.pageSize = Number(size);
        this.pageIndex = 0;
        this.loadPage();
    }

    goToPage(index: number): void {
        const total = this.page?.pages ?? 0;
        if (index < 0 || index >= total || index === this.pageIndex) {
            return;
        }
        this.pageIndex = index;
        this.loadPage();
    }

    goSettings(): void {
        this.router.navigate(['/settings'], { queryParams: { new: true } });
    }

    private loadPage(): void {
        if (this.partnerId === null) {
            return;
        }

        if (this.firstLoad) {
            this.spinner.open();
        } else {
            this.listLoading = true;
        }

        const search = this.searchControl.value.trim();
        this.portalService.getPortalsPaged(this.partnerId, {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            ...(search ? { search } : {}),
            ...(this.statusFilter !== 'all' ? { active: this.statusFilter === 'active' } : {})
        }).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
              next: page => {
                  this.page = page;
                  this.portalService.portals.next(page.portals);
                  this.closeLoading();
              },
              error: err => {
                  this.closeLoading();
                  this.toast.showError(err);
              }
          });
    }

    private closeLoading(): void {
        if (this.firstLoad) {
            this.spinner.close();
            this.firstLoad = false;
        }
        this.listLoading = false;
    }
}
